import { createRouter, createWebHistory } from 'vue-router'
import type { RouteRecordRaw } from 'vue-router'
import { useAuthStore } from '@/stores/auth'

// Import page components
import LandingPage from '@/views/public/LandingPage.vue'
import Projects from '@/views/member/Projects.vue'
import Help from '@/views/member/Help.vue'
import CallbackPage from '@/views/auth/CallbackPage.vue'
import LogoutPage from '@/views/auth/LogoutPage.vue'

// Import registration page components
import DeveloperRegister from '@/views/developer/RegisterPage.vue'
import AuditorRegister from '@/views/auditor/RegisterPage.vue'
import TraderRegister from '@/views/trader/RegisterPage.vue'
import GovernmentRegister from '@/views/government/RegisterPage.vue'

// Import dashboard page components
import DeveloperDashboard from '@/views/developer/DashboardPage.vue'
import AuditorDashboard from '@/views/auditor/DashboardPage.vue'
import TraderDashboard from '@/views/trader/DashboardPage.vue'
import GovernmentDashboard from '@/views/government/DashboardPage.vue'

// Import project page components
import SubmitPin from '@/views/developer/project/SubmitPin.vue'
import SubmitPdd from '@/views/developer/project/SubmitPdd.vue'
import SubmitPcn from '@/views/developer/project/SubmitPcn.vue'
import LoaStatus from '@/views/developer/project/LoaStatus.vue'
import ValidationStatus from '@/views/developer/project/ValidationStatus.vue'
import RegistrationCertificate from '@/views/developer/project/RegistrationCertificate.vue'
import VerificationStatus from '@/views/developer/project/VerificationStatus.vue'
import ProjectDetail from '@/views/developer/project/ProjectDetail.vue'
import RenewCreditingPeriod from '@/views/developer/project/RenewCreditingPeriod.vue'
import ProjectAmendment from '@/views/developer/project/ProjectAmendment.vue'
import ReportReversal from '@/views/developer/project/ReportReversal.vue'
import SdgReporting from '@/views/developer/project/SdgReporting.vue'
import LegacyRegistration from '@/views/developer/LegacyRegistration.vue'
import UploadReports from '@/views/developer/UploadReports.vue'
import RequestCredits from '@/views/developer/RequestCredits.vue'
import BrowseRegistry from '@/views/developer/BrowseRegistry.vue'
import MyProjects from '@/views/developer/MyProjects.vue'
import Credits from '@/views/developer/Credits.vue'
import RetireCredits from '@/views/developer/RetireCredits.vue'
import TransferCredits from '@/views/developer/TransferCredits.vue'
import Profile from '@/views/developer/Profile.vue'
import CorrespondingAdjustments from '@/views/developer/CorrespondingAdjustments.vue'
import Grievances from '@/views/developer/Grievances.vue'
import Notifications from '@/views/developer/Notifications.vue'
import Payments from '@/views/developer/Payments.vue'

// Import government page components
import ApplicationQueue from '@/views/government/ApplicationQueue.vue'
import Certificates from '@/views/government/Certificates.vue'
import RegistryOversight from '@/views/government/RegistryOversight.vue'
import GrievanceManagement from '@/views/government/GrievanceManagement.vue'
import DoeLicenses from '@/views/government/DoeLicenses.vue'
import UnfcccReporting from '@/views/government/UnfcccReporting.vue'
import Administration from '@/views/government/Administration.vue'

// Define routes
const routes: RouteRecordRaw[] = [
  {
    path: '/',
    name: 'Home',
    component: LandingPage,
    meta: {
      title: 'Carbon Registry - Home'
    }
  },
  {
    path: '/projects',
    name: 'Projects',
    component: Projects,
    meta: {
      title: 'My Projects - Carbon Registry',
      requiresAuth: true
    }
  },
  {
    path: '/help',
    name: 'Help',
    component: Help,
    meta: {
      title: 'Help & Support - Carbon Registry',
      requiresAuth: true
    }
  },
  {
    path: '/auth/callback',
    name: 'AuthCallback',
    component: CallbackPage,
    meta: {
      title: 'Signing In...'
    }
  },
  {
    path: '/auth/logout',
    name: 'AuthLogout',
    component: LogoutPage,
    meta: {
      title: 'Signed Out'
    }
  },
  // Registration routes
  {
    path: '/developer/register',
    name: 'DeveloperRegister',
    component: DeveloperRegister,
    meta: {
      title: 'Project Developer Registration - Carbon Registry'
    }
  },
  {
    path: '/auditor/register',
    name: 'AuditorRegister',
    component: AuditorRegister,
    meta: {
      title: 'Auditor (DOE) Registration - Carbon Registry'
    }
  },
  {
    path: '/trader/register',
    name: 'TraderRegister',
    component: TraderRegister,
    meta: {
      title: 'Credit Holder/Trader Registration - Carbon Registry'
    }
  },
  {
    path: '/government/register',
    name: 'GovernmentRegister',
    component: GovernmentRegister,
    meta: {
      title: 'Government/DNA Registration - Carbon Registry'
    }
  },
  // Dashboard routes
  {
    path: '/developer/dashboard',
    name: 'DeveloperDashboard',
    component: DeveloperDashboard,
    meta: {
      title: 'Developer Dashboard - Carbon Registry',
      requiresAuth: true
    }
  },
  {
    path: '/auditor/dashboard',
    name: 'AuditorDashboard',
    component: AuditorDashboard,
    meta: {
      title: 'Auditor Dashboard - Carbon Registry',
      requiresAuth: true
    }
  },
  {
    path: '/trader/dashboard',
    name: 'TraderDashboard',
    component: TraderDashboard,
    meta: {
      title: 'Trader Dashboard - Carbon Registry',
      requiresAuth: true
    }
  },
  {
    path: '/government/dashboard',
    name: 'GovernmentDashboard',
    component: GovernmentDashboard,
    meta: {
      title: 'Government Dashboard - Carbon Registry',
      requiresAuth: true
    }
  },
  // Project routes
  {
    path: '/developer/project/submit-pin',
    name: 'SubmitPin',
    component: SubmitPin,
    meta: {
      title: 'Submit PIN - Carbon Registry',
      requiresAuth: true
    }
  },
  {
    path: '/developer/project/submit-pdd',
    name: 'SubmitPdd',
    component: SubmitPdd,
    meta: {
      title: 'Submit PDD - Carbon Registry',
      requiresAuth: true
    }
  },
  {
    path: '/developer/legacy-registration',
    name: 'LegacyRegistration',
    component: LegacyRegistration,
    meta: {
      title: 'Legacy Registration - Carbon Registry',
      requiresAuth: true
    }
  },
  {
    path: '/developer/upload-reports',
    name: 'UploadReports',
    component: UploadReports,
    meta: {
      title: 'Upload Reports - Carbon Registry',
      requiresAuth: true
    }
  },
  {
    path: '/developer/request-credits',
    name: 'RequestCredits',
    component: RequestCredits,
    meta: {
      title: 'Request Credits - Carbon Registry',
      requiresAuth: true
    }
  },
  {
    path: '/developer/browse-registry',
    name: 'BrowseRegistry',
    component: BrowseRegistry,
    meta: {
      title: 'Browse Registry - Carbon Registry',
      requiresAuth: true
    }
  },
  // New developer project routes
  {
    path: '/developer/project/submit-pcn',
    name: 'SubmitPcn',
    component: SubmitPcn,
    meta: { title: 'Submit PCN - Carbon Registry', requiresAuth: true }
  },
  {
    path: '/developer/project/loa-status',
    name: 'LoaStatus',
    component: LoaStatus,
    meta: { title: 'LoA Status - Carbon Registry', requiresAuth: true }
  },
  {
    path: '/developer/project/validation-status',
    name: 'ValidationStatus',
    component: ValidationStatus,
    meta: { title: 'Validation Status - Carbon Registry', requiresAuth: true }
  },
  {
    path: '/developer/project/registration-certificate',
    name: 'RegistrationCertificate',
    component: RegistrationCertificate,
    meta: { title: 'Registration Certificate - Carbon Registry', requiresAuth: true }
  },
  {
    path: '/developer/project/verification-status',
    name: 'VerificationStatus',
    component: VerificationStatus,
    meta: { title: 'Verification Status - Carbon Registry', requiresAuth: true }
  },
  {
    path: '/developer/project/:id',
    name: 'ProjectDetail',
    component: ProjectDetail,
    meta: { title: 'Project Detail - Carbon Registry', requiresAuth: true }
  },
  {
    path: '/developer/project/renew-crediting-period',
    name: 'RenewCreditingPeriod',
    component: RenewCreditingPeriod,
    meta: { title: 'Renew Crediting Period - Carbon Registry', requiresAuth: true }
  },
  {
    path: '/developer/project/amend',
    name: 'ProjectAmendment',
    component: ProjectAmendment,
    meta: { title: 'Project Amendment - Carbon Registry', requiresAuth: true }
  },
  {
    path: '/developer/project/report-reversal',
    name: 'ReportReversal',
    component: ReportReversal,
    meta: { title: 'Report Reversal - Carbon Registry', requiresAuth: true }
  },
  {
    path: '/developer/project/sdg-reporting',
    name: 'SdgReporting',
    component: SdgReporting,
    meta: { title: 'SDG Co-benefits Reporting - Carbon Registry', requiresAuth: true }
  },
  // New developer account/portfolio routes
  {
    path: '/developer/projects',
    name: 'MyProjects',
    component: MyProjects,
    meta: { title: 'My Projects - Carbon Registry', requiresAuth: true }
  },
  {
    path: '/developer/credits',
    name: 'Credits',
    component: Credits,
    meta: { title: 'Credit Portfolio - Carbon Registry', requiresAuth: true }
  },
  {
    path: '/developer/retire-credits',
    name: 'RetireCredits',
    component: RetireCredits,
    meta: { title: 'Retire Credits - Carbon Registry', requiresAuth: true }
  },
  {
    path: '/developer/transfer-credits',
    name: 'TransferCredits',
    component: TransferCredits,
    meta: { title: 'Transfer Credits - Carbon Registry', requiresAuth: true }
  },
  {
    path: '/developer/profile',
    name: 'DeveloperProfile',
    component: Profile,
    meta: { title: 'Account Profile - Carbon Registry', requiresAuth: true }
  },
  {
    path: '/developer/corresponding-adjustments',
    name: 'CorrespondingAdjustments',
    component: CorrespondingAdjustments,
    meta: { title: 'Corresponding Adjustments - Carbon Registry', requiresAuth: true }
  },
  {
    path: '/developer/grievances',
    name: 'Grievances',
    component: Grievances,
    meta: { title: 'Grievances - Carbon Registry', requiresAuth: true }
  },
  {
    path: '/developer/notifications',
    name: 'DeveloperNotifications',
    component: Notifications,
    meta: { title: 'Notifications - Carbon Registry', requiresAuth: true }
  },
  {
    path: '/developer/payments',
    name: 'Payments',
    component: Payments,
    meta: { title: 'Payment History - Carbon Registry', requiresAuth: true }
  },
  // Government routes
  {
    path: '/government/application-queue',
    name: 'ApplicationQueue',
    component: ApplicationQueue,
    meta: {
      title: 'Application Queue - Carbon Registry',
      requiresAuth: true
    }
  },
  {
    path: '/government/certificates',
    name: 'Certificates',
    component: Certificates,
    meta: {
      title: 'Certificates - Carbon Registry',
      requiresAuth: true
    }
  },
  {
    path: '/government/registry-oversight',
    name: 'RegistryOversight',
    component: RegistryOversight,
    meta: {
      title: 'Registry Oversight - Carbon Registry',
      requiresAuth: true
    }
  },
  {
    path: '/government/grievance-management',
    name: 'GrievanceManagement',
    component: GrievanceManagement,
    meta: {
      title: 'Grievance Management - Carbon Registry',
      requiresAuth: true
    }
  },
  {
    path: '/government/doe-licenses',
    name: 'DoeLicenses',
    component: DoeLicenses,
    meta: {
      title: 'DOE Licenses - Carbon Registry',
      requiresAuth: true
    }
  },
  {
    path: '/government/unfccc-reporting',
    name: 'UnfcccReporting',
    component: UnfcccReporting,
    meta: {
      title: 'UNFCCC Reporting - Carbon Registry',
      requiresAuth: true
    }
  },
  {
    path: '/government/administration',
    name: 'Administration',
    component: Administration,
    meta: {
      title: 'Administration - Carbon Registry',
      requiresAuth: true
    }
  }
]

// Create router instance
const router = createRouter({
  history: createWebHistory(),
  routes,
  // Scroll behavior for better UX
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    } else {
      return { top: 0 }
    }
  }
})

// Navigation guards
router.beforeEach(async (to, from, next) => {
  try {
    // Set page title
    if (to.meta?.title) {
      document.title = to.meta.title as string
    }

    // Check if route requires authentication (either explicit meta or member path)
    const requiresAuth = to.meta?.requiresAuth || to.path.startsWith('/member')
    
    if (requiresAuth) {
      const authStore = useAuthStore()
      
      // Check if user is authenticated
      if (!authStore.isLoggedIn) {
        // Try to restore auth state from storage
        const isAuthenticated = await authStore.checkAuthState()
        
        if (!isAuthenticated) {
          // Redirect to login
          await authStore.login()
          return next(false) // Prevent navigation
        }
      }
      
      // Check if access token is expired and refresh if needed
      if (authStore.isTokenExpired()) {
        const refreshResult = await authStore.refreshTokens()
        
        if (!refreshResult.success) {
          // Refresh failed, redirect to login
          await authStore.login()
          return next(false) // Prevent navigation
        }
      }
    }

    next()
  } catch (error) {
    console.error('Navigation guard error:', error)
    next(false) // Prevent navigation on error
  }
})

export default router